import { useState } from 'react'
import { apiClient } from '../lib/api-client'
import { rememberPendingPurchase, purchaseReturnUrls } from '../lib/pending-purchase'
import { SUPPORT_EMAIL } from '../lib/legal'

/** Mirrors MEMOIR_NAME_MAX (cloudflare/workers/utils/memoir-checkout.ts). */
const NAME_MAX = 100

export interface NewMemoirModalProps {
  /** The Memoirs that exist now, so the return page can tell which one is new. */
  knownMemoirIds: string[]
  onClose: () => void
}

/**
 * Buy another Memoir (SOL-204): name it, then go to Stripe Checkout. The
 * Memoir itself is created by the webhook after payment settles, which is why
 * this records the pending purchase before leaving — see pending-purchase.ts.
 *
 * The price is stated VAT-exclusive because that is how it is charged: a
 * business with a VAT ID is reverse-charged, everyone else pays their
 * country's VAT on top.
 */
export default function NewMemoirModal({ knownMemoirIds, onClose }: NewMemoirModalProps) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = name.trim()

  async function buy(): Promise<void> {
    if (!trimmed || busy) return
    setBusy(true)
    setError(null)
    try {
      const { successUrl, cancelUrl } = purchaseReturnUrls(window.location.origin)
      const { url } = await apiClient.createMemoirCheckout(trimmed, successUrl, cancelUrl)
      rememberPendingPurchase(knownMemoirIds)
      window.location.href = url
    } catch {
      setError(`Could not start checkout. Please try again, or write to ${SUPPORT_EMAIL}.`)
      setBusy(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div
        className="modal-content new-memoir-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-memoir-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="new-memoir-title">A new Memoir</h3>
        <p className="new-memoir-modal__lede">
          One complete delivery to one client, kept safe for them. €30 + VAT, paid once.
        </p>
        <label className="new-memoir-modal__label" htmlFor="new-memoir-name">
          What should it be called?
        </label>
        <input
          id="new-memoir-name"
          type="text"
          value={name}
          maxLength={NAME_MAX}
          placeholder="e.g. Giulia & Marco, 14 June"
          autoFocus
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void buy()
            if (e.key === 'Escape' && !busy) onClose()
          }}
        />
        {error && <p className="new-memoir-modal__error" role="alert">{error}</p>}
        <div className="new-memoir-modal__actions">
          <button type="button" className="new-memoir-modal__cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="new-memoir-modal__buy" onClick={() => void buy()} disabled={!trimmed || busy}>
            {busy ? 'Opening checkout…' : 'Continue to payment'}
          </button>
        </div>
      </div>
    </div>
  )
}
